"use strict";

var _Object$defineProperty = require("@babel/runtime-corejs3/core-js/object/define-property");
var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault").default;
_Object$defineProperty(exports, "__esModule", {
  value: true
});
exports.createAuth = createAuth;
exports.createDbAuthClient = createDbAuthClient;
var _stringify = _interopRequireDefault(require("@babel/runtime-corejs3/core-js/json/stringify"));
var _auth = require("@redwoodjs/auth");
const TOKEN_CACHE_TIME = 5000;

// This is the middleware-edition auth function
// Overrides the default getCurrentUser to fetch it from middleware instead
function createMiddlewareAuth(dbAuthClient, customProviderHooks) {
  return (0, _auth.createAuthentication)(dbAuthClient, {
    ...customProviderHooks,
    // @MARK This is key! 👇
    useCurrentUser: customProviderHooks?.useCurrentUser ?? (() => (0, _auth.getCurrentUserFromMiddleware)(dbAuthClient.getAuthUrl()))
  });
}
function createAuth(dbAuthClient, customProviderHooks) {
  if (dbAuthClient.middlewareAuthEnabled) {
    return createMiddlewareAuth(dbAuthClient, customProviderHooks);
  }
  return (0, _auth.createAuthentication)(dbAuthClient, customProviderHooks);
}
function createDbAuthClient({
  webAuthn,
  dbAuthUrl,
  fetchConfig,
  middleware = RWJS_ENV.RWJS_EXP_STREAMING_SSR
} = {}) {
  const credentials = fetchConfig?.credentials || 'same-origin';
  webAuthn?.setAuthApiUrl(dbAuthUrl);
  let getTokenPromise;
  let lastTokenCheckAt = new Date('1970-01-01T00:00:00');
  let cachedToken;
  const getDbAuthUrl = () => {
    if (dbAuthUrl) {
      return dbAuthUrl;
    }
    return middleware ? `/middleware/dbauth` : `${RWJS_API_URL}/auth`;
  };
  const resetAndFetch = async (...params) => {
    resetTokenCache();
    return fetch(...params);
  };
  const isTokenCacheExpired = () => {
    const now = new Date();
    return now.getTime() - lastTokenCheckAt.getTime() > TOKEN_CACHE_TIME;
  };
  const resetTokenCache = () => {
    lastTokenCheckAt = new Date('1970-01-01T00:00:00');
    cachedToken = null;
  };
  const forgotPassword = async username => {
    const response = await resetAndFetch(getDbAuthUrl(), {
      credentials,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: (0, _stringify.default)({
        username,
        method: 'forgotPassword'
      })
    });
    return response.json();
  };
  const getToken = async () => {
    // Middleware auth providers doesn't need a token
    if (middleware) {
      return null;
    }
    // Return the existing fetch promise, so that parallel calls
    // to getToken only cause a single fetch
    if (getTokenPromise) {
      return getTokenPromise;
    }
    if (isTokenCacheExpired()) {
      getTokenPromise = fetch(`${getDbAuthUrl()}?method=getToken`, {
        credentials
      }).then(response => response.text()).then(tokenText => {
        lastTokenCheckAt = new Date();
        cachedToken = tokenText.length === 0 ? null : tokenText;
        return cachedToken;
      }).catch(() => {
        return null;
      }).finally(() => {
        getTokenPromise = null;
      });
      return getTokenPromise;
    }
    return cachedToken;
  };
  const login = async ({
    username,
    password
  }) => {
    const response = await resetAndFetch(getDbAuthUrl(), {
      credentials,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: (0, _stringify.default)({
        username,
        password,
        method: 'login'
      })
    });
    return response.json();
  };
  const logout = async () => {
    await resetAndFetch(getDbAuthUrl(), {
      credentials,
      method: 'POST',
      body: (0, _stringify.default)({
        method: 'logout'
      })
    });
    return true;
  };
  const resetPassword = async attributes => {
    const response = await resetAndFetch(getDbAuthUrl(), {
      credentials,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: (0, _stringify.default)({
        ...attributes,
        method: 'resetPassword'
      })
    });
    return response.json();
  };
  const signup = async attributes => {
    const response = await resetAndFetch(getDbAuthUrl(), {
      credentials,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: (0, _stringify.default)({
        ...attributes,
        method: 'signup'
      })
    });
    return response.json();
  };
  const validateResetToken = async resetToken => {
    const response = await resetAndFetch(getDbAuthUrl(), {
      credentials,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: (0, _stringify.default)({
        resetToken,
        method: 'validateResetToken'
      })
    });
    return response.json();
  };

  /*
   * dbAuth doesn't implement the concept of userMetadata. We used to return the token (i.e. userId) as the userMetadata.
   */
  const getUserMetadata = async () => {
    return middleware ? () => {} : getToken();
  };
  return {
    type: 'dbAuth',
    client: webAuthn,
    login,
    logout,
    signup,
    getToken,
    getUserMetadata,
    forgotPassword,
    resetPassword,
    validateResetToken,
    // 👇 New methods for middleware auth
    // so we can get the dbAuthUrl in getCurrentUserFromMiddleware
    getAuthUrl: getDbAuthUrl,
    // This is so that we can skip fetching getCurrentUser in reauthenticate
    middlewareAuthEnabled: middleware
  };
}